import { useState, useEffect, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { FaListOl, FaDollarSign, FaArrowUp, FaArrowDown, FaEquals, FaGripLinesVertical } from 'react-icons/fa';

import { formatCurrencyShort, parseCurrencyToNumber, formatDateBucket, transformBreakdownToDonutData, generateDateBuckets, getAutoResolution } from '../../../common/utils';
import TableMetric from '../../../containers/Reports/metric';
import DoughnutCard from '../../Charts/Doughnut/card';
import BarLineCombo from '../../Charts/Combo/barLine';


export default function PipelineFunded( { data, filters } ) {
    const [ count, setCount ] = useState( 0 );
    const [ vol, setVol ] = useState( 0 );
    const [ max, setMax ] = useState( 0 );
    const [ min, setMin ] = useState( 0 );
    const [ median, setMedian ] = useState( 0 );
    const [ mean, setMean ] = useState( 0 );
    const [ metrics, setMetrics ] = useState( [
        { label: 'Funded', value: 0, tooltip: '# of Funded Loans', icon: <FaListOl style={{ color: '#ff7a59', marginRight: 8 }} /> },
        { label: 'Volume', value: 0, tooltip: 'Total Amount of Funded Loans', icon: <FaDollarSign style={{ color: '#ff7a59', marginRight: 8 }} /> },
        { label: 'Highest', value: 0, tooltip: 'Largest Single Funded Loan', icon: <FaArrowUp style={{ color: '#ff7a59', marginRight: 8 }} /> },
        { label: 'Lowest', value: 0, tooltip: 'Smallest Single Funded Loan', icon: <FaArrowDown style={{ color: '#ff7a59', marginRight: 8 }} /> },
        { label: 'Average', value: 0, tooltip: 'Average Amount of Funded Loans', icon: <FaEquals style={{ color: '#ff7a59', marginRight: 8 }} /> },
        { label: 'Median', value: 0, tooltip: 'Median Amount of Funded Loans', icon: <FaGripLinesVertical style={{ color: '#ff7a59', marginRight: 8 }} /> },
    ] );
    const [ purposeMode, setPurposeMode ] = useState( 'units' );
    const [ breakdown, setBreakdown ] = useState( {} );
    const [ summaryData, setSummaryData ] = useState( {
        totalUnits: 0,
        totalVolume: 0,
        purposeBreakdown: {},
        typeBreakdown: {}
    } );
    const [ trendData, setTrendData ] = useState( [] );
    const [ resolution, setResolution ] = useState( 'monthly' );
    
    const getFundedSummaryData = useCallback( () => {
        const summary = {
            totalUnits: 0,
            totalVolume: 0,
            max: 0,
            min: 0,
            mean: 0,
            median: 0,
            loanPurpose: {}, // { Purchase: { units: 0, volume: 0 }, Refi: { ... } }
            loanType: {},    // { Conventional: { units: 0, volume: 0 }, FHA: { ... } }
            loanSource: {},  // { 'Past Client': { units: 0, volume: 0 }, 'Realtor Referral': { ... } }
            loanAmounts: [],
        };
      
        const trendMap = {}; // { '2025-07': { units: 0, volume: 0 } }
        const allBuckets = generateDateBuckets( filters, resolution );
        for ( const bucket of allBuckets ) {
            trendMap[bucket] = { x: bucket, units: 0, volume: 0 };
        }
        
        for ( const loan of data ) {
            const fundedDate = new Date( loan.fundedDate?.match( 'T' ) ? loan.fundedDate : `${loan.fundedDate}T12:00:00` );
            if ( isNaN( fundedDate ) ) continue;
        
            // summary.totalUnits += 1;
        
            const amount = parseCurrencyToNumber( loan.amount );
            // summary.totalVolume += amount;
            summary.loanAmounts.push( amount );
        
            // Loan purpose
            const purpose = loan.purpose || 'Unknown';
            if (!summary.loanPurpose[purpose]) {
                summary.loanPurpose[purpose] = { units: 0, volume: 0 };
            }
            summary.loanPurpose[purpose].units += 1;
            summary.loanPurpose[purpose].volume += amount;
        
            // Loan type
            const type = loan.loanType || 'Unknown';
            if (!summary.loanType[type]) {
                summary.loanType[type] = { units: 0, volume: 0 };
            }
            summary.loanType[type].units += 1;
            summary.loanType[type].volume += amount;

            // Loan source
            const source = loan.loanSource || 'Unknown';
            if (!summary.loanSource[source]) {
                summary.loanSource[source] = { units: 0, volume: 0 };
            }
            summary.loanSource[source].units += 1;
            summary.loanSource[source].volume += amount;
        
            // Trend (grouped by month/week/day)
            const bucket = formatDateBucket( fundedDate, resolution);
            console.log( bucket, loan );
            if ( trendMap[bucket] ) {
                trendMap[bucket].units += 1;
                trendMap[bucket].volume += amount;
            }
        }

        summary.loanAmounts.sort( ( a, b ) => a - b ); // For median + min/max
        summary.totalVolume = summary.loanAmounts.reduce( ( sum, val ) => sum + val, 0 );
        summary.totalUnits = summary.loanAmounts.length;
        summary.mean = summary.totalUnits ? summary.totalVolume / summary.totalUnits : 0;
        summary.min = summary.loanAmounts[0] || 0;
        summary.max = summary.loanAmounts[summary.totalUnits - 1] || 0;
        summary.median = summary.totalUnits ? ( summary.totalUnits % 2 === 0 ? ( summary.loanAmounts[summary.totalUnits / 2 - 1] + summary.loanAmounts[summary.totalUnits / 2] ) / 2 : summary.loanAmounts[Math.floor( summary.totalUnits / 2 )] ) : 0;
      
        const trendData = Object.values( trendMap ).sort( ( a, b ) => new Date( a.x ) - new Date( b.x ) );
      
        return {
            summaryData: summary,
            trendData,
        };
    }, [ data, resolution, filters ] );

    useEffect( () => {
        if ( data?.length ) {
            const summary = getFundedSummaryData( 'weekly' );
            setSummaryData( summary.summaryData );
            setTrendData( summary.trendData );

            setCount( summary.summaryData.totalUnits );
            setVol( formatCurrencyShort( summary.summaryData.totalVolume ) );
            setMin( formatCurrencyShort( summary.summaryData.min ) );
            setMax( formatCurrencyShort( summary.summaryData.max ) );
            setMedian( formatCurrencyShort( summary.summaryData.median ) );
            setMean( formatCurrencyShort( summary.summaryData.mean ) );
        } else {
            setSummaryData( {
                totalUnits: 0,
                totalVolume: 0,
                min: 0,
                max: 0,
                median: 0,
                mean: 0,
                purposeBreakdown: {},
                typeBreakdown: {}
            } );
            setTrendData( [] );
            setCount( 0 );
            setVol( 0 );
            setMin( 0 );
            setMax( 0 );
            setMedian( 0 );
            setMean( 0 );
        }
    }, [ data, getFundedSummaryData ] );

    useEffect( () => {
        setMetrics( [
            { label: 'Funded', value: count, tooltip: '# of Funded Loans', icon: <FaListOl style={{ color: '#ff7a59', marginRight: 8 }} /> },
            { label: 'Volume', value: vol, tooltip: 'Total Amount of Funded Loans', icon: <FaDollarSign style={{ color: '#ff7a59', marginRight: 8 }} /> },
            { label: 'Highest', value: max, tooltip: 'Largest Single Funded Loan', icon: <FaArrowUp style={{ color: '#ff7a59', marginRight: 8 }} /> },
            { label: 'Lowest', value: min, tooltip: 'Smallest Single Funded Loan', icon: <FaArrowDown style={{ color: '#ff7a59', marginRight: 8 }} /> },
            { label: 'Average', value: mean, tooltip: 'Average Amount of Funded Loans', icon: <FaEquals style={{ color: '#ff7a59', marginRight: 8 }} /> },
            { label: 'Median', value: median, tooltip: 'Median Amount of Funded Loans', icon: <FaGripLinesVertical style={{ color: '#ff7a59', marginRight: 8 }} /> },
        ] );
    }, [ count, vol, min, max, mean, median ] );

    useEffect( () => {
        if ( filters ) {
            setResolution( getAutoResolution( filters ) )
        }
    }, [ filters ] );


    return (
        <Container fluid className='w-100'>
            <Row className='pb-4 text-center'>
                <Col className='ps-0' sm={2}>
                    <Box className='h-100' display="flex" gap={2} justifyContent="space-evenly" sx={ { flexDirection: { xs: 'column', md: 'row' } } }>
                        <Card variant="outlined" className='py-1 px-0 me-2 w-100' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Stack 
                                direction="column" 
                                justifyContent="space-evenly" 
                                spacing={0} 
                                sx={{ width: 1, flex: 1, minHeight: 0 }} 
                                divider={ <Divider orientation="horizontal" flexItem style={ { backgroundColor: 'grey' } } /> }
                            >
                            {
                                metrics.map( ( metric, idx ) => (
                                    <TableMetric key={`metric-${idx}`} metric={metric} idx={idx} showDivider={ idx < ( metrics.length - 1 ) } direction='vertical' />
                                ) )
                            }
                            </Stack>
                        </Card>
                    </Box>
                </Col>
                <Col className='px-0'>
                    <Card className="h-100">
                        <Card.Body>
                            <Row className="d-flex justify-content-between align-items-center mb-2">
                                <Col>
                                    <Card.Title as="h6" className="mb-0">Funded Trends</Card.Title>
                                </Col>
                            </Row>
                            <Row>
                                <Col style={ { height: '400px' } }>
                                    <BarLineCombo data={trendData} height={400} />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row className='pb-4'>
                <Col className='px-0'>
                    <Card className="h-100">
                        <Card.Body>
                            <Row className="d-flex justify-content-between align-items-center mb-2">
                                <Col>
                                    <Card.Title as="h6" className="mb-0">Funded Distributions</Card.Title>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={12} md={4}>
                                    <DoughnutCard 
                                        centerLabel="Purpose"
                                        data={transformBreakdownToDonutData( summaryData.loanPurpose ?? {}, purposeMode )}
                                        mode={purposeMode}
                                        height={200}
                                    />
                                </Col>
                                <Col sm={12} md={4}>
                                    <DoughnutCard 
                                        centerLabel="Type"
                                        data={transformBreakdownToDonutData( summaryData.loanType ?? {}, purposeMode )}
                                        mode={purposeMode}
                                        height={200}
                                    />
                                </Col>
                                <Col sm={12} md={4}>
                                    <DoughnutCard 
                                        centerLabel="Source"
                                        data={transformBreakdownToDonutData( summaryData.loanSource ?? {}, purposeMode )}
                                        mode={purposeMode}
                                        height={200}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row className='pb-4'>
                
            </Row>
        </Container>
    );
  }